<script setup lang="ts">
import type { SectionMediaLargeRecord } from '~/types/datocms'

const props = defineProps<{
  data: SectionMediaLargeRecord
  moduleIndex: number
}>()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.title
)
</script>

<template>
  <div
    class="container grid grid-cols-2 gap-8 pb-15 pt-12 lg:gap-5 lg:pb-80 lg:pt-40"
    :class="invertedStyles"
  >
    <div class="col-span-full lg:col-span-1">
      <Heading
        v-if="data.title"
        :level="level"
        :is-semantic="isSemantic"
        :size="3"
        class="mb-2 uppercase lg:mb-16"
      >
        <TextReveal>{{ data.title }}</TextReveal>
      </Heading>
    </div>
    <div class="col-span-full lg:col-span-1 lg:col-start-1">
      <div
        v-if="data.html"
        class="break-inside-avoid font-medium lg:text-xl xl:columns-2 xl:gap-5 [&>p]:mb-6 [&>p]:break-inside-avoid"
        v-html="data.html"
      />
      <CallToAction
        v-if="Array.isArray(data.buttons) && data.buttons.length"
        :data="data.buttons"
        class="lg:mt-16"
      />
    </div>
    <div class="col-span-full flex flex-col items-end gap-4 lg:relative lg:col-span-1 lg:gap-0">
      <Image
        class="aspect-9/10 w-full object-cover"
        loading="lazy"
        :src="
          data.mediaAssets[0]?.responsiveImage
            ? data.mediaAssets[0].responsiveImage?.src
            : data.mediaAssets[0]?.url
        "
        :srcset="
          data.mediaAssets[0]?.responsiveImage
            ? data.mediaAssets[0].responsiveImage?.webpSrcSet
            : undefined
        "
        :width="
          data.mediaAssets[0]?.responsiveImage
            ? data.mediaAssets[0].responsiveImage?.width
            : (data.mediaAssets[0]?.width ?? 660)
        "
        :height="
          data.mediaAssets[0]?.responsiveImage
            ? data.mediaAssets[0].responsiveImage?.height
            : (data.mediaAssets[0]?.height ?? 495)
        "
        :alt="data.mediaAssets[0]?.alt ?? ''"
      />
      <Image
        class="aspect-21/7 w-full max-w-5xl object-cover lg:absolute lg:-bottom-20 lg:right-7 lg:w-[68vw]"
        :src="data.mediaAssets[1]?.srcNarrow"
        loading="lazy"
        :width="
          data.mediaAssets[1]?.responsiveImage
            ? data.mediaAssets[1].responsiveImage?.width
            : (data.mediaAssets[1]?.width ?? 660)
        "
        :height="
          data.mediaAssets[1]?.responsiveImage
            ? data.mediaAssets[1].responsiveImage?.height
            : (data.mediaAssets[1]?.height ?? 495)
        "
        :alt="data.mediaAssets[1]?.alt ?? ''"
      />
      <div
        class="ambi-img aspect-square w-full object-cover lg:absolute lg:-bottom-40 lg:right-[57vw] lg:w-[23vw] lg:max-w-xs"
        :style="{
          backgroundImage: data.mediaAssets[2]?.responsiveImage
            ? `url(${data.mediaAssets[2].responsiveImage?.src})`
            : `url(${data.mediaAssets[2]?.url})`
        }"
      >
        <Image
          class="aspect-square w-full object-cover"
          :src="
            data.mediaAssets[2]?.responsiveImage
              ? data.mediaAssets[2].responsiveImage?.src
              : data.mediaAssets[2]?.url
          "
          :width="
            data.mediaAssets[2]?.responsiveImage
              ? data.mediaAssets[2].responsiveImage?.width
              : (data.mediaAssets[2]?.width ?? 660)
          "
          :height="
            data.mediaAssets[2]?.responsiveImage
              ? data.mediaAssets[2].responsiveImage?.height
              : (data.mediaAssets[2]?.height ?? 495)
          "
          :alt="data.mediaAssets[2]?.alt ?? ''"
        />
      </div>
    </div>
  </div>
</template>
